'use client';
// /pipeline?tab=protection — Build Protection action log + recent regressions.
//
// Surfaces:
//   1. Today's delta vs the last archived snapshot (regressions + improvements)
//   2. Action log (last 200 entries) — fix runs, verifications, escalations
//   3. Current escalations (if any) — what's been iMessage'd
//
// Steve directive 2026-05-20: "i am spending too much time fixing problems
// and not enough time managing money." This tab is the proof that the
// pipeline is auditing + healing itself.

import { useResource } from '@/lib/use-resource';

interface Regression {
  kind: string;
  severity: 'critical' | 'warning';
  subject: string;
  yesterdayPct: number | null;
  todayPct: number;
  deltaPp: number;
  message: string;
  peerGroupHint?: string;
}

interface DeltaReport {
  todayDate: string;
  baselineDate: string | null;
  regressions: Regression[];
  improvements: Regression[];
  summary: { criticalCount: number; warningCount: number; deltaMissingPct: number | null; deltaApplicable: number | null };
}

interface LogEntry {
  ts: string;
  kind: 'detect' | 'fix-run' | 'fix-verified' | 'fix-failed' | 'escalation';
  symptom?: string;
  fix?: string;
  message: string;
  exitCode?: number;
}

interface ProtectionResp {
  delta: DeltaReport;
  log: LogEntry[];
  escalations: { ts?: string; escalations: Array<{ ts: string; symptom: string; severity: string; message: string }> } | null;
}

function fmtTs(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function kindTone(kind: LogEntry['kind']): string {
  switch (kind) {
    case 'fix-verified': return 'text-emerald-300 border-emerald-400/30';
    case 'fix-failed':   return 'text-red-300 border-red-400/40';
    case 'escalation':   return 'text-red-300 border-red-400/40 bg-red-400/5';
    case 'fix-run':      return 'text-amber-300 border-amber-400/30';
    case 'detect':       return 'text-[#94a3b8] border-[#1e293b]';
    default:             return 'text-[#94a3b8]';
  }
}

export function ProtectionTab() {
  const { data, error } = useResource<ProtectionResp>('/api/build-protection', 30_000);

  if (error) return <div className="mt-6 text-red-300 text-sm">Failed to load: {String(error)}</div>;
  if (!data) return <div className="mt-6 text-sm text-[#64748b]">Loading…</div>;

  const { delta, log, escalations } = data;
  const todayLog = log.slice().reverse();  // newest first

  return (
    <div className="space-y-6">
      {/* Headline strip */}
      <div className="border border-[#1e293b] rounded-lg overflow-hidden bg-[#0f141c]">
        <div className="px-4 py-3 bg-[#0a0e14] border-b border-[#1e293b]">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#c9a84c]">Build Protection</div>
          <div className="text-[10px] text-[#64748b] mt-0.5">
            Day-over-day regression tracker + self-heal playbook + iMessage escalation.
            Today {delta.todayDate} {delta.baselineDate ? `vs ${delta.baselineDate}` : '(no prior baseline)'}.
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 px-4 py-3 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[#64748b]">Critical</div>
            <div className={`text-[18px] tabular-nums font-semibold ${delta.summary.criticalCount > 0 ? 'text-red-300' : 'text-emerald-300'}`}>
              {delta.summary.criticalCount}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[#64748b]">Warning</div>
            <div className={`text-[18px] tabular-nums font-semibold ${delta.summary.warningCount > 0 ? 'text-amber-300' : 'text-emerald-300'}`}>
              {delta.summary.warningCount}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[#64748b]">Δ Missing</div>
            <div className={`text-[18px] tabular-nums font-semibold ${
              delta.summary.deltaMissingPct === null ? 'text-[#94a3b8]' :
              delta.summary.deltaMissingPct <= 0 ? 'text-emerald-300' :
              'text-red-300'
            }`}>
              {delta.summary.deltaMissingPct === null ? '—' :
               (delta.summary.deltaMissingPct > 0 ? '+' : '') + delta.summary.deltaMissingPct.toFixed(2) + 'pp'}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[#64748b]">Δ Universe</div>
            <div className={`text-[18px] tabular-nums font-semibold ${
              delta.summary.deltaApplicable === null ? 'text-[#94a3b8]' :
              delta.summary.deltaApplicable >= 0 ? 'text-emerald-300' :
              'text-red-300'
            }`}>
              {delta.summary.deltaApplicable === null ? '—' :
               (delta.summary.deltaApplicable > 0 ? '+' : '') + delta.summary.deltaApplicable}
            </div>
          </div>
        </div>
      </div>

      {/* Regressions */}
      {delta.regressions.length > 0 && (
        <div className="border border-[#1e293b] rounded-lg overflow-hidden bg-[#0f141c]">
          <div className="px-4 py-2 bg-[#0a0e14] border-b border-[#1e293b]">
            <div className="text-[10px] uppercase tracking-wider text-red-300 font-semibold">
              Regressions ({delta.regressions.length})
            </div>
          </div>
          <div className="divide-y divide-[#1e293b]/40">
            {delta.regressions.map((r, i) => (
              <div key={`${r.kind}-${r.subject}-${i}`} className="px-4 py-2 text-[11px]">
                <div className="flex items-baseline gap-3">
                  <span className={`text-[9px] uppercase tracking-wider font-semibold ${r.severity === 'critical' ? 'text-red-300' : 'text-amber-300'}`}>
                    {r.severity}
                  </span>
                  <span className="font-mono text-[#cbd5e1] font-semibold">{r.subject}</span>
                  <span className="text-[#64748b] text-[10px]">{r.kind}</span>
                  {r.yesterdayPct !== null && (
                    <span className="ml-auto text-[10px] tabular-nums text-[#94a3b8]">
                      {r.yesterdayPct.toFixed(1)}% → {r.todayPct.toFixed(1)}% ({r.deltaPp.toFixed(1)}pp)
                    </span>
                  )}
                </div>
                <div className="text-[#94a3b8] text-[10px] mt-1 leading-snug">{r.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Improvements */}
      {delta.improvements.length > 0 && (
        <div className="border border-emerald-400/20 rounded-lg overflow-hidden bg-emerald-400/5">
          <div className="px-4 py-2 border-b border-emerald-400/20">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">
              Improvements ({delta.improvements.length})
            </div>
          </div>
          <div className="divide-y divide-emerald-400/10">
            {delta.improvements.map((r, i) => (
              <div key={`imp-${r.subject}-${i}`} className="px-4 py-1.5 text-[10px]">
                <span className="font-mono text-emerald-300/90">{r.subject}</span>
                <span className="ml-3 text-[#94a3b8]">{r.yesterdayPct?.toFixed(1)}% → {r.todayPct.toFixed(1)}% (+{r.deltaPp.toFixed(1)}pp)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active escalations */}
      {escalations?.escalations && escalations.escalations.length > 0 && (
        <div className="border border-red-400/40 rounded-lg overflow-hidden bg-red-400/5">
          <div className="px-4 py-2 border-b border-red-400/20">
            <div className="text-[10px] uppercase tracking-wider text-red-300 font-semibold">
              Active Escalations — iMessaged
            </div>
          </div>
          <div className="divide-y divide-red-400/10">
            {escalations.escalations.map((e, i) => (
              <div key={`esc-${i}`} className="px-4 py-2 text-[11px]">
                <div className="flex items-baseline gap-3">
                  <span className="text-[9px] uppercase tracking-wider text-red-300 font-semibold">{e.severity}</span>
                  <span className="font-mono text-[#cbd5e1]">{e.symptom}</span>
                  <span className="ml-auto text-[10px] text-[#64748b]">{fmtTs(e.ts)}</span>
                </div>
                <div className="text-[#cbd5e1] text-[10px] mt-1 leading-snug">{e.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action log */}
      <div className="border border-[#1e293b] rounded-lg overflow-hidden bg-[#0f141c]">
        <div className="px-4 py-2 bg-[#0a0e14] border-b border-[#1e293b] flex items-baseline gap-3">
          <div className="text-[10px] uppercase tracking-wider text-[#94a3b8] font-semibold">Action Log</div>
          <div className="text-[9px] text-[#64748b]">Last {todayLog.length} entries · newest first</div>
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {todayLog.length === 0 ? (
            <div className="px-4 py-3 text-[11px] text-[#64748b]">No actions logged yet.</div>
          ) : (
            todayLog.map((entry, i) => (
              <div key={`log-${i}`} className={`px-4 py-1.5 text-[10px] border-l-2 ${kindTone(entry.kind)} my-px`}>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-[9px] text-[#64748b] tabular-nums">{fmtTs(entry.ts)}</span>
                  <span className={`text-[9px] uppercase tracking-wider font-semibold ${kindTone(entry.kind).split(' ')[0]}`}>
                    {entry.kind}
                  </span>
                  {entry.symptom && <span className="font-mono text-[#cbd5e1] text-[10px]">{entry.symptom}</span>}
                  {entry.fix && <span className="text-amber-300/80 text-[10px]">[{entry.fix}]</span>}
                  {entry.exitCode !== undefined && entry.exitCode !== 0 && (
                    <span className="text-red-300 text-[9px]">exit={entry.exitCode}</span>
                  )}
                </div>
                <div className="text-[#94a3b8] text-[10px] mt-0.5 ml-1 leading-snug">{entry.message}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
